import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  apiService,
  RankingWeightsInput,
  SimulatedProduct,
  RankingWeightsConfig
} from '../services/api';

interface Variation {
  id: string;
  label: string;
  weights: RankingWeightsInput;
  products: SimulatedProduct[];
  loading: boolean;
}

const DEFAULT_WEIGHTS: RankingWeightsInput = {
  revenue: 0.45,
  quantity: 0.20,
  stock: 0.15,
  category: 0.08,
  brand: 0.07,
  potential_sales: 0.03,
  price: 0.02,
};

const WEIGHT_LABELS: Record<keyof RankingWeightsInput, string> = {
  revenue: 'Receita',
  quantity: 'Quantidade',
  stock: 'Stock',
  category: 'Categoria',
  brand: 'Marca',
  potential_sales: 'Potencial',
  price: 'Preço',
};

const formatSnapshotLabel = (ts: string): string => {
  try {
    return new Date(ts).toLocaleString('pt-PT', {
      timeZone: 'Europe/Lisbon',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

const formatEur = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
};

interface NewVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (label: string, weights: RankingWeightsInput) => void;
  initialWeights: RankingWeightsInput;
  defaultLabel: string;
}

const formatPctDisplay = (decimalValue: number): string => {
  const pct = decimalValue * 100;
  // Usa representação mais curta possível (sem trailing zeros) — evita "2.0" quando quero "2"
  return parseFloat(pct.toFixed(4)).toString();
};

const NewVariationModal: React.FC<NewVariationModalProps> = ({
  isOpen, onClose, onSubmit, initialWeights, defaultLabel,
}) => {
  const [label, setLabel] = useState(defaultLabel);
  const [weights, setWeights] = useState<RankingWeightsInput>(initialWeights);
  // Estado de texto dos inputs (separado do valor numérico) — permite
  // escrever "2" sem ficar "2.0", escrever "2." sem apagar ponto, etc.
  const [pctText, setPctText] = useState<Record<keyof RankingWeightsInput, string>>({
    revenue: formatPctDisplay(initialWeights.revenue),
    quantity: formatPctDisplay(initialWeights.quantity),
    stock: formatPctDisplay(initialWeights.stock),
    category: formatPctDisplay(initialWeights.category),
    brand: formatPctDisplay(initialWeights.brand),
    potential_sales: formatPctDisplay(initialWeights.potential_sales),
    price: formatPctDisplay(initialWeights.price),
  });

  useEffect(() => {
    if (isOpen) {
      setLabel(defaultLabel);
      setWeights(initialWeights);
      setPctText({
        revenue: formatPctDisplay(initialWeights.revenue),
        quantity: formatPctDisplay(initialWeights.quantity),
        stock: formatPctDisplay(initialWeights.stock),
        category: formatPctDisplay(initialWeights.category),
        brand: formatPctDisplay(initialWeights.brand),
        potential_sales: formatPctDisplay(initialWeights.potential_sales),
        price: formatPctDisplay(initialWeights.price),
      });
    }
  }, [isOpen, defaultLabel, initialWeights]);

  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const validSum = Math.abs(total - 1.0) < 0.001;

  const handlePctChange = (key: keyof RankingWeightsInput, pctValue: string) => {
    // Preserva o texto tal como o user escreve (inclui "2.", "0.", "", ".5")
    setPctText((prev) => ({ ...prev, [key]: pctValue }));
    const num = parseFloat(pctValue);
    if (!isNaN(num)) {
      setWeights((prev) => ({ ...prev, [key]: num / 100 }));
    } else if (pctValue === '' || pctValue === '.') {
      setWeights((prev) => ({ ...prev, [key]: 0 }));
    }
  };

  const handleSubmit = () => {
    if (!validSum || !label.trim()) return;
    onSubmit(label.trim(), weights);
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Nova Variação de Ranking</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Nome da variação</label>
              <input
                type="text"
                className="form-control"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Ex: Mais peso em stock"
              />
            </div>

            <h6 className="mt-4 mb-3">Pesos (% — soma deve ser 100)</h6>
            <div className="row g-3">
              {(Object.keys(WEIGHT_LABELS) as Array<keyof RankingWeightsInput>).map((key) => (
                <div key={key} className="col-md-6">
                  <label className="form-label">{WEIGHT_LABELS[key]}</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      className="form-control"
                      step="1"
                      min="0"
                      max="100"
                      value={pctText[key]}
                      onChange={(e) => handlePctChange(key, e.target.value)}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <small className="text-muted">
                    Decimal: <code>{weights[key].toFixed(4)}</code>
                  </small>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 rounded" style={{ backgroundColor: validSum ? '#d4edda' : '#f8d7da' }}>
              <strong>Total: {(total * 100).toFixed(1)}%</strong>
              {' '}
              {validSum ? (
                <span className="text-success">✓ válido</span>
              ) : (
                <span className="text-danger">
                  (precisa somar 100% — diferença {((1.0 - total) * 100).toFixed(1)}pp)
                </span>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!validSum || !label.trim()}
            >
              Criar e Simular
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RankingSimulatorPage: React.FC = () => {
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [baseSnapshot, setBaseSnapshot] = useState<string>('');
  const [topN, setTopN] = useState<number>(50);
  const [variations, setVariations] = useState<Variation[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [compareA, setCompareA] = useState<string>('base');
  const [compareB, setCompareB] = useState<string>('');
  const [baseProducts, setBaseProducts] = useState<SimulatedProduct[]>([]);
  const [loadingBase, setLoadingBase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWeights, setCurrentWeights] = useState<RankingWeightsInput>(DEFAULT_WEIGHTS);

  // Load snapshots list
  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.getRankingSnapshots({ limit: 50 });
        setSnapshots(res.snapshots || []);
        if (res.snapshots && res.snapshots.length > 0 && !baseSnapshot) {
          setBaseSnapshot(res.snapshots[0]);
        }
      } catch (e) {
        console.error(e);
        setError('Erro ao carregar snapshots');
      }
    })();
  }, []);

  // Load current active weights as default for modal
  useEffect(() => {
    (async () => {
      try {
        const config: RankingWeightsConfig = await apiService.getRankingWeights();
        if (config?.weights) {
          const w = config.weights;
          setCurrentWeights({
            revenue: w.revenue ?? DEFAULT_WEIGHTS.revenue,
            quantity: w.quantity ?? DEFAULT_WEIGHTS.quantity,
            stock: w.stock ?? DEFAULT_WEIGHTS.stock,
            category: w.category ?? DEFAULT_WEIGHTS.category,
            brand: w.brand ?? DEFAULT_WEIGHTS.brand,
            potential_sales: w.potential_sales ?? DEFAULT_WEIGHTS.potential_sales,
            price: w.price ?? DEFAULT_WEIGHTS.price,
          });
        }
      } catch {
        // Keep defaults if config endpoint fails
      }
    })();
  }, []);

  // Load base ranking with current production weights
  const loadBase = useCallback(async () => {
    if (!baseSnapshot) return;
    setLoadingBase(true);
    setError(null);
    try {
      const res = await apiService.simulateRankingWeights({
        snapshot_timestamp: baseSnapshot,
        weights: currentWeights,
        top_n: topN,
      });
      setBaseProducts(res.products || []);
    } catch (e: any) {
      console.error(e);
      setError('Erro ao carregar ranking base: ' + (e?.response?.data?.error || e.message));
    } finally {
      setLoadingBase(false);
    }
  }, [baseSnapshot, topN, currentWeights]);

  useEffect(() => {
    loadBase();
    setVariations([]);  // reset variations when base changes
  }, [baseSnapshot, topN, loadBase]);

  const handleCreateVariation = async (label: string, weights: RankingWeightsInput) => {
    if (!baseSnapshot) return;
    setShowModal(false);
    const newId = `var_${Date.now()}`;
    const newVar: Variation = {
      id: newId,
      label,
      weights,
      products: [],
      loading: true,
    };
    setVariations((prev) => [...prev, newVar]);

    try {
      const res = await apiService.simulateRankingWeights({
        snapshot_timestamp: baseSnapshot,
        weights,
        top_n: topN,
      });
      setVariations((prev) => prev.map((v) =>
        v.id === newId ? { ...v, products: res.products || [], loading: false } : v
      ));
    } catch (e: any) {
      console.error(e);
      setError('Erro ao simular: ' + (e?.response?.data?.error || e.message));
      setVariations((prev) => prev.filter((v) => v.id !== newId));
    }
  };

  const removeVariation = (id: string) => {
    setVariations((prev) => prev.filter((v) => v.id !== id));
    if (compareA === id) setCompareA('base');
    if (compareB === id) setCompareB('');
  };

  // Build SKU → position map for each variation (and base)
  const getPositionMap = (products: SimulatedProduct[]): Map<string, { position: number; product: SimulatedProduct }> => {
    const map = new Map<string, { position: number; product: SimulatedProduct }>();
    products.forEach((p) => {
      map.set(p.sku, { position: p.new_position, product: p });
    });
    return map;
  };

  const basePosMap = getPositionMap(baseProducts);
  const variationMaps = variations.map((v) => ({
    id: v.id,
    label: v.label,
    map: getPositionMap(v.products),
    loading: v.loading,
  }));

  // Rows = union of top N SKUs from base + all variations
  const allSkus = new Set<string>();
  baseProducts.slice(0, topN).forEach((p) => allSkus.add(p.sku));
  variations.forEach((v) => v.products.slice(0, topN).forEach((p) => allSkus.add(p.sku)));

  // Sort rows by base position (SKUs not in base at the end)
  const sortedSkus = Array.from(allSkus).sort((a, b) => {
    const posA = basePosMap.get(a)?.position ?? 99999;
    const posB = basePosMap.get(b)?.position ?? 99999;
    return posA - posB;
  });

  // Comparison: positions of SKU in ranking A and B
  const getComparisonData = (id: string): Map<string, { position: number; product: SimulatedProduct }> | null => {
    if (id === 'base') return basePosMap;
    const v = variations.find((x) => x.id === id);
    return v ? getPositionMap(v.products) : null;
  };

  const mapA = getComparisonData(compareA);
  const mapB = compareB ? getComparisonData(compareB) : null;
  const showDiff = mapA && mapB && compareA !== compareB;

  const labelA = compareA === 'base' ? 'Base' : variations.find((v) => v.id === compareA)?.label || '';
  const labelB = compareB === 'base' ? 'Base' : variations.find((v) => v.id === compareB)?.label || '';

  const renderPositionCell = (sku: string, map: Map<string, { position: number; product: SimulatedProduct }> | null): JSX.Element => {
    if (!map) return <span className="text-muted">-</span>;
    const entry = map.get(sku);
    if (!entry) return <span className="text-muted">—</span>;
    return (
      <div className="d-flex flex-column align-items-center">
        <strong>#{entry.position}</strong>
        <small className="text-muted" style={{ fontSize: '0.75em' }}>
          {entry.product.new_score.toFixed(4)}
        </small>
      </div>
    );
  };

  const renderDiff = (sku: string): JSX.Element => {
    if (!mapA || !mapB) return <span>-</span>;
    const a = mapA.get(sku);
    const b = mapB.get(sku);
    if (!a && !b) return <span className="text-muted">—</span>;
    if (!a) return (
      <span className="text-success" title={`Entrou no top ${topN} em B (estava fora do top ${topN} em A)`}>
        ↑ entra top {topN}
      </span>
    );
    if (!b) return (
      <span className="text-danger" title={`Caiu fora do top ${topN} em B (estava em #${a.position} em A)`}>
        ↓ cai fora top {topN}
      </span>
    );
    const delta = a.position - b.position;  // positivo: subiu de A para B
    if (delta === 0) return <span className="text-muted">=</span>;
    if (delta > 0) return <span className="text-success">↑ +{delta}</span>;
    return <span className="text-danger">↓ {delta}</span>;
  };

  return (
    <div>
      <h1 className="mb-4">🎛️ Ranking Simulator</h1>
      <p className="text-muted">
        Teste variações de pesos do ranking sem afectar produção. Cada variação recalcula o <code>final_score</code>{' '}
        usando os campos <code>norm_*</code> armazenados no snapshot, mantendo a <code>penalty_applied</code> original.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Snapshot base</label>
              <select
                className="form-select"
                value={baseSnapshot}
                onChange={(e) => setBaseSnapshot(e.target.value)}
              >
                {snapshots.map((s) => (
                  <option key={s} value={s}>{formatSnapshotLabel(s)}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Top N</label>
              <input
                type="number"
                className="form-control"
                min="10"
                max="500"
                step="10"
                value={topN}
                onChange={(e) => setTopN(Math.max(10, Math.min(500, parseInt(e.target.value) || 50)))}
              />
            </div>
            <div className="col-md-5 text-end">
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
                disabled={!baseSnapshot || loadingBase}
              >
                + Nova Variação
              </button>
            </div>
          </div>
        </div>
      </div>

      {variations.length >= 1 && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Comparar A</label>
                <select
                  className="form-select"
                  value={compareA}
                  onChange={(e) => setCompareA(e.target.value)}
                >
                  <option value="base">Base (produção)</option>
                  {variations.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Comparar B</label>
                <select
                  className="form-select"
                  value={compareB}
                  onChange={(e) => setCompareB(e.target.value)}
                >
                  <option value="">-- selecciona --</option>
                  <option value="base">Base (produção)</option>
                  {variations.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                {showDiff && (
                  <div className="alert alert-info mb-0 py-2">
                    A comparar <strong>{labelA}</strong> → <strong>{labelB}</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingBase && <div className="alert alert-info">A carregar ranking base...</div>}

      {baseProducts.length > 0 && (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th>SKU</th>
                    <th>Produto</th>
                    <th>Categoria</th>
                    <th className="text-center" style={{ minWidth: '100px' }}>Base</th>
                    {variations.map((v) => (
                      <th key={v.id} className="text-center" style={{ minWidth: '120px', position: 'relative' }}>
                        <div
                          className="d-flex justify-content-between align-items-center variation-header"
                          style={{ cursor: 'help' }}
                        >
                          <span className="text-decoration-underline" style={{ textDecorationStyle: 'dotted' }}>
                            {v.label}
                          </span>
                          <button
                            className="btn btn-sm btn-link text-danger p-0 ms-1"
                            style={{ fontSize: '0.7em' }}
                            onClick={() => removeVariation(v.id)}
                            title="Remover variação"
                          >
                            ✕
                          </button>
                          <div className="variation-tooltip">
                            <strong>{v.label}</strong>
                            <table className="mt-2 mb-0 small w-100">
                              <tbody>
                                {(Object.keys(WEIGHT_LABELS) as Array<keyof RankingWeightsInput>).map((k) => (
                                  <tr key={k}>
                                    <td className="text-start pe-3">{WEIGHT_LABELS[k]}</td>
                                    <td className="text-end fw-bold">{(v.weights[k] * 100).toFixed(1)}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </th>
                    ))}
                    {showDiff && (
                      <th className="text-center" style={{ minWidth: '140px' }}>
                        Diff ({labelA} → {labelB})
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedSkus.slice(0, topN).map((sku, idx) => {
                    const baseEntry = basePosMap.get(sku);
                    const product = baseEntry?.product;
                    return (
                      <tr key={sku}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>
                          <Link to={`/products/${sku}`} className="text-decoration-none">
                            <code>{sku}</code>
                          </Link>
                        </td>
                        <td className="small text-truncate" style={{ maxWidth: '250px' }}>
                          {product?.product_name || '-'}
                        </td>
                        <td className="small text-truncate" style={{ maxWidth: '150px' }}>
                          {product?.categoria_nome || '-'}
                        </td>
                        <td className="text-center">{renderPositionCell(sku, basePosMap)}</td>
                        {variationMaps.map((vm) => (
                          <td key={vm.id} className="text-center">
                            {vm.loading ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              renderPositionCell(sku, vm.map)
                            )}
                          </td>
                        ))}
                        {showDiff && <td className="text-center">{renderDiff(sku)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <NewVariationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreateVariation}
        initialWeights={currentWeights}
        defaultLabel={`Variação ${variations.length + 1}`}
      />
    </div>
  );
};

export default RankingSimulatorPage;
